// KB-UPLOAD-2 (founder feedback 2026-10-30): what an uploaded doc is ("Add as" Reference, SOP or
// Page, each a doc kind) and whom it is shared with besides its space — more spaces, and people
// or DEs directly — each with a level: Read only, Can comment, Can edit. A DE is shared with by
// its seat, so it reads (`read_doc`) or keeps current (`update_doc`) what its seat is given.
import { Button, InfoTip, NativeSelect, NativeSelectOption, WorkerBadge } from "@companyos/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  BookMarkedIcon,
  ListChecksIcon,
  type LucideIcon,
  NotebookPenIcon,
  PlusIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useId } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { digitalEmployeesQuery, peopleQuery } from "../queries";
import { DOC_LEVEL_HELP } from "./space-shares";

/** SHARE-EVERYONE: + `manager` — Full access (edits, shares and manages the doc). */
export type DocLevel = "reader" | "viewer" | "editor" | "manager";

/**
 * SHARE-EVERYONE: the names every Share entry's level picker shows. `viewer` (Can comment) is
 * only offered on an entry that already has it. The most permissive entry wins.
 */
export const ENTRY_LEVEL_LABEL: Record<DocLevel, string> = {
  reader: "Can view",
  viewer: "Can comment",
  editor: "Can edit",
  manager: "Full access",
};
const PERSON_LEVELS: readonly DocLevel[] = ["reader", "editor", "manager"];
/** Everyone in the company: view or edit, never Full access. */
const EVERYONE_LEVELS: readonly DocLevel[] = ["reader", "editor"];
const levelsFor = (offered: readonly DocLevel[], current: DocLevel): DocLevel[] =>
  offered.includes(current) ? [...offered] : [current, ...offered];
export type AddAs = "kb" | "sop" | "page";

/** "Add as": the three things an upload can be, with the defaults each brings. */
export const ADD_AS: readonly {
  kind: AddAs;
  label: string;
  hint: string;
  icon: LucideIcon;
  /** Read only for everyone but its managers. */
  readOnly: boolean;
  /** The level a space or person added in "Share with" starts at. */
  spaceLevel: Exclude<DocLevel, "manager">;
  memberLevel: DocLevel;
}[] = [
  {
    kind: "kb",
    label: "Reference",
    hint: "Books, guides. Read only for whoever you share with.",
    icon: BookMarkedIcon,
    readOnly: true,
    spaceLevel: "reader",
    memberLevel: "reader",
  },
  {
    kind: "sop",
    label: "Procedure",
    hint: "An SOP. DEs you pick keep it current.",
    icon: ListChecksIcon,
    readOnly: false,
    spaceLevel: "viewer",
    memberLevel: "editor",
  },
  {
    kind: "page",
    label: "Page",
    hint: "A working doc, like any other.",
    icon: NotebookPenIcon,
    readOnly: false,
    spaceLevel: "editor",
    memberLevel: "editor",
  },
];

export const addAsOf = (kind: AddAs): (typeof ADD_AS)[number] =>
  ADD_AS.find((a) => a.kind === kind) ?? (ADD_AS[0] as (typeof ADD_AS)[number]);

/** "Add as" as a radio group of three cards. */
export function AddAsPicker({
  value,
  onChange,
  disabled = false,
}: {
  value: AddAs;
  onChange: (next: AddAs) => void;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5" data-slot="add-as">
      <div className="flex items-center gap-1">
        <span id={`${id}-label`} className="text-sm font-medium">
          Add as
        </span>
        <InfoTip label="About Add as">
          Reference goes to the Knowledge base, read only for whoever you share it with (agents too)
          unless you give someone edit. Procedure is an SOP that the DEs you pick may update. Page
          is a normal working doc. You can change it later on the doc.
        </InfoTip>
      </div>
      <div role="radiogroup" aria-labelledby={`${id}-label`} className="grid grid-cols-3 gap-2">
        {ADD_AS.map((a) => {
          const Icon = a.icon;
          const on = a.kind === value;
          return (
            // biome-ignore lint/a11y/useSemanticElements: a card-styled radio of three choices.
            <button
              key={a.kind}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={disabled}
              onClick={() => onChange(a.kind)}
              className="flex flex-col items-start gap-1 rounded-md border border-border px-2.5 py-2 text-left text-sm hover:bg-muted aria-checked:border-primary aria-checked:bg-primary-soft disabled:opacity-60"
            >
              <span className="flex items-center gap-1.5 font-medium">
                <Icon aria-hidden="true" className="size-4 text-muted-foreground" />
                {a.label}
              </span>
              <span className="text-xs leading-snug text-muted-foreground">{a.hint}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export type MemberInput = {
  /** `company`: everyone in the company (principal_id = the company id). */
  principal_kind: "person" | "seat" | "company";
  principal_id: string;
  level: DocLevel;
};
export type MemberOption = {
  kind: "person" | "seat";
  id: string;
  name: string;
  /** A DE's seat title. */
  title?: string;
};

/** The people (active persons) and DEs (by seat) a doc can be shared with. */
export function useMemberOptions(api: Api, company: CompanyAccess, enabled: boolean) {
  const people = useInfiniteQuery({ ...peopleQuery(api, company.id), enabled });
  const des = useInfiniteQuery({ ...digitalEmployeesQuery(api, company.id), enabled });
  // Codex r1: everyone, not the first page only.
  useEffect(() => {
    if (enabled && people.hasNextPage && !people.isFetchingNextPage) void people.fetchNextPage();
  }, [enabled, people.hasNextPage, people.isFetchingNextPage, people.fetchNextPage]);
  useEffect(() => {
    if (enabled && des.hasNextPage && !des.isFetchingNextPage) void des.fetchNextPage();
  }, [enabled, des.hasNextPage, des.isFetchingNextPage, des.fetchNextPage]);
  const persons: MemberOption[] = (people.data?.pages.flatMap((p) => p.data) ?? [])
    .filter((p) => p.kind === "person" && p.status === "active")
    .map((p) => ({ kind: "person" as const, id: p.id, name: p.display_name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const seats: MemberOption[] = (des.data?.pages.flatMap((p) => p.data) ?? [])
    .filter((d) => d.status !== "retired" && d.seat)
    .map((d) => ({
      kind: "seat" as const,
      id: d.seat?.id ?? "",
      name: d.display_name,
      title: d.seat?.title ?? "",
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return { persons, seats, all: [...seats, ...persons] };
}

const keyOf = (m: { principal_kind?: string; kind?: string; principal_id?: string; id?: string }) =>
  `${m.principal_kind ?? m.kind}:${m.principal_id ?? m.id}`;

const optionLabel = (o: MemberOption) => (o.kind === "seat" ? `${o.name} · ${o.title}` : o.name);

/**
 * People and DEs: one row per person or DE (who, level, remove) and an Add button; with
 * `everyone` also the "Everyone" option next to it: on, a chip "Everyone in <Company>" with its
 * own level and a remove button. Everyone is the entry `{ principal_kind: "company" }` in `value`.
 */
export function MemberSharesField({
  options,
  value: all,
  onChange: setAll,
  defaultLevel,
  disabled = false,
  everyone,
}: {
  options: { persons: MemberOption[]; seats: MemberOption[]; all: MemberOption[] };
  value: readonly MemberInput[];
  onChange: (next: MemberInput[]) => void;
  defaultLevel: DocLevel;
  disabled?: boolean;
  everyone?: { companyId: string; companyName: string };
}) {
  const id = useId();
  const entry = all.find((m) => m.principal_kind === "company") ?? null;
  const value = all.filter((m) => m.principal_kind !== "company");
  const onChange = (next: MemberInput[]) => setAll(entry ? [...next, entry] : next);
  const taken = new Set(value.map(keyOf));
  const unused = options.all.filter((o) => !taken.has(keyOf(o)));
  const set = (i: number, patch: Partial<MemberInput>) =>
    onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const nameOf = (m: MemberInput) => {
    const o = options.all.find((x) => keyOf(x) === keyOf(m));
    return o ? o.name : m.principal_kind === "seat" ? "A DE" : "A person";
  };
  const group = (label: string, list: MemberOption[], current: MemberInput) => {
    const shown = list.filter((o) => keyOf(o) === keyOf(current) || !taken.has(keyOf(o)));
    return shown.length ? (
      <optgroup label={label}>
        {shown.map((o) => (
          <NativeSelectOption key={keyOf(o)} value={keyOf(o)}>
            {optionLabel(o)}
          </NativeSelectOption>
        ))}
      </optgroup>
    ) : null;
  };
  return (
    <div className="flex flex-col gap-1.5" data-slot="member-shares">
      <span id={`${id}-label`} className="sr-only">
        People and DEs
      </span>
      {/* biome-ignore lint/a11y/useSemanticElements: a list of share rows, labelled as a group. */}
      <ul
        role="group"
        aria-labelledby={`${id}-label`}
        className="m-0 flex list-none flex-col gap-1.5 p-0"
      >
        {value.map((m, i) => (
          <li key={keyOf(m)} className="flex items-center gap-1.5" data-slot="member-share-row">
            {m.principal_kind === "seat" ? <WorkerBadge kind="de" size="sm" /> : null}
            <NativeSelect
              aria-label={`Shared with ${i + 1}`}
              className="min-w-0 flex-1"
              value={keyOf(m)}
              disabled={disabled}
              onChange={(e) => {
                const [kind, pid] = e.target.value.split(":");
                set(i, {
                  principal_kind: kind as MemberInput["principal_kind"],
                  principal_id: pid ?? "",
                });
              }}
            >
              {group("DEs", options.seats, m)}
              {group("People", options.persons, m)}
              {options.all.some((o) => keyOf(o) === keyOf(m)) ? null : (
                <NativeSelectOption value={keyOf(m)}>{nameOf(m)}</NativeSelectOption>
              )}
            </NativeSelect>
            <NativeSelect
              aria-label={`Access for ${nameOf(m)}`}
              className="w-36"
              value={m.level}
              disabled={disabled}
              onChange={(e) => set(i, { level: e.target.value as DocLevel })}
            >
              {levelsFor(PERSON_LEVELS, m.level).map((l) => (
                <NativeSelectOption key={l} value={l}>
                  {ENTRY_LEVEL_LABEL[l]}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Stop sharing with ${nameOf(m)}`}
              title="Remove"
              disabled={disabled}
              onClick={() => onChange(value.filter((_, j) => j !== i))}
            >
              <XIcon aria-hidden="true" />
            </Button>
          </li>
        ))}
      </ul>
      {entry && everyone ? (
        <div
          data-slot="everyone-chip"
          className="flex items-center gap-1.5 rounded-md border border-border bg-primary-soft py-1 pr-1 pl-2"
        >
          <UsersIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-sm font-medium">
            Everyone in {everyone.companyName}
          </span>
          <InfoTip label="About Everyone">
            Everyone in the company can read this. Pick Can edit to let them change it too. Agents
            whose reach includes company-level content see it as well.
          </InfoTip>
          <NativeSelect
            aria-label="Access for Everyone"
            className="w-36"
            value={entry.level}
            disabled={disabled}
            onChange={(e) =>
              setAll(
                all.map((m) =>
                  m.principal_kind === "company" ? { ...m, level: e.target.value as DocLevel } : m,
                ),
              )
            }
          >
            {levelsFor(EVERYONE_LEVELS, entry.level).map((l) => (
              <NativeSelectOption key={l} value={l}>
                {ENTRY_LEVEL_LABEL[l]}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Stop sharing with Everyone"
            title="Remove"
            disabled={disabled}
            onClick={() => setAll(all.filter((m) => m.principal_kind !== "company"))}
          >
            <XIcon aria-hidden="true" />
          </Button>
        </div>
      ) : null}
      {unused.length || (everyone && !entry) ? (
        <div className="flex flex-wrap items-center gap-1">
          {unused.length ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() => {
                const first = unused[0];
                if (first)
                  onChange([
                    ...value,
                    { principal_kind: first.kind, principal_id: first.id, level: defaultLevel },
                  ]);
              }}
            >
              <PlusIcon aria-hidden="true" />
              Add person or DE
            </Button>
          ) : null}
          {everyone && !entry ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() =>
                setAll([
                  ...value,
                  { principal_kind: "company", principal_id: everyone.companyId, level: "reader" },
                ])
              }
            >
              <UsersIcon aria-hidden="true" />
              Everyone
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export { DOC_LEVEL_HELP };
